import type { ValueLabelOptionsGroup } from 'common/components/Elements/Form/InputSelect/InputSelect';
import { arrayFlat,arrayIntersection, arrayLast } from 'common/utilities/core/array';
import { deepClone } from 'common/utilities/core/object';
import { isLightTable } from 'core/api/api.typesGuards';
import * as I from 'core/api/coreApiClient';
import { DEFAULT_SETTING_ID } from 'core/components/Elements/Table/ModalColsVisibility/ModalColsVisibility';
import type { ColumnsState } from 'core/components/Elements/Table/Table/Table';
import type { ValueLabelOption } from 'core/utilities/core.types';
import { convertNodeToFlatNodes } from 'core/utilities/FilterNodeTree/FilterNodeTree';
import { flattenSections } from 'core/utilities/page/page';
import I18n from 'i18n/en-US';

export function paginatedItems<T>(page: number, pageSize: number, items: T[]) {
  const start = page * pageSize;
  const end = start + pageSize;
  return items.slice(start, end);
}

export const hasGroupSelectedAll = (group: I.Group, visiblePropertyIds: string[]) => {
  const ids = group.infos.map((info) => info.id);
  const commonIds = arrayIntersection(ids, visiblePropertyIds);
  return ids.length === commonIds.length;
};

export const defaultFilterNodeOperator = I.FilterRowOperatorConstants.AND;

export const defaultFilterNode: I.FilterNode = {
  leaf: false,
  filterRowOperator: defaultFilterNodeOperator,
  left: {
    leaf: true,
    filterRow: {
      name: 'NAME', // deprecated, will be removed from types
    },
  },
  right: {
    leaf: true,
    filterRow: {
      name: 'NAME', // deprecated, will be removed from types
    },
  },
};

/**
 * Concat all meta groups infos into one array
 * @param metaInfo
 * @param addGroupLabelToInfoLabel add group label as a prefix i.e. "Name" becomes "Project > Name"
 */
export function metaInfoInfos(metaInfo: I.Meta, addGroupLabelToInfoLabel?: boolean) {
  return metaInfo.groups.reduce<I.Info[]>((output, group) => output.concat(group.infos.map((info) => ({
    ...info,
    label: addGroupLabelToInfoLabel ? `${group.label} > ${info.label}` : info.label,
  }))), []);
}

export const activeTableFilterMessages = (metaInfo: I.Meta, filter?: I.TableFilter) => {
  const rootNode = filter?.root;

  if (!rootNode) return undefined;

  const infos = metaInfoInfos(metaInfo);
  const flatNodes = convertNodeToFlatNodes(rootNode);

  const messages = arrayFlat(flatNodes
    .map((n) => n.data.filterRow?.messages?.map((m) => (
      { ...m, text: `${(infos.find((i) => i.id === n.data.filterRow?.propertyId) as I.Info).label}: ${m.text}` }
    )))
    .filter((m) => m) as I.Message[][]);

  return messages.length > 0 ? messages : undefined;
};

export function hasRowAnyValueSet(row: I.Row) {
  return row.values.some((value) => value.item && (value.item as I.BaseItem).value !== undefined);
}

export const getlightTableColumnsInfos = (section: I.Section) => {
  if (section.metaInfo && section.metaInfo.groups && section.metaInfo.groups[0]) {
    return section.metaInfo.groups[0].infos;
  }
  return undefined;
};

export function isValidLightTableRow(infos: I.Info[], row: I.Row) {
  // New rows without any values are ignored
  if (!row.id && !hasRowAnyValueSet(row)) return true;

  const columnsValidity = infos.map((info) => {
    if (info.constraint && info.constraint.mandatory) {
      const value = row.values.find((v) => v.id === info.id);

      if (value) {
        return (value.item as I.BaseItem)?.value !== undefined;
      }

      return false;
    }
    return true;
  });

  return !columnsValidity.some((validColumn) => !validColumn);
}

export const filterOutLightTableEmptyRows = (sections: I.Section[]) => sections.map((section) => {
  const newSection: I.Section = { ...section };

  newSection.sections = filterOutLightTableEmptyRows(newSection.sections);

  if (newSection.lightTable && section.lightTable?.canAddRows) {
    newSection.lightTable = {
      ...newSection.lightTable,
      rows: newSection.lightTable.rows.filter((row) => hasRowAnyValueSet(row)),
    };
  }

  return newSection;
});

export function isValidLightTable(section: I.Section) {
  if (isLightTable(section.lightTable)) {
    const infos = getlightTableColumnsInfos(section);
    return !section.lightTable.rows.some((row) => !isValidLightTableRow(infos || [], row));
  }

  return true;
}

export function lightTableSectionsConstraintsValidations(sections: I.Section[]): I.Section[] {
  return flattenSections(sections)
    .filter((s) => s.type === I.SectionTypeConstants.LightTable)
    .filter((s) => !isValidLightTable(s));
}

type InfoIdToGroup = Record<string, I.Group>;

/**
 * Re-group infos keeping the infos array order.
 * Meaning that multiple of the same groups can be created if infos are not one after another.
 *
 * Example:
 * initialGroups:
 *   group A: info 1, 2, 3
 *   group B: info 4, 5, 6
 * orderedInfos: 1, 2, 4, 5, 6, 3
 * Output:
 *   group A: info 1, 2
 *   group B: info 4, 5, 6
 *   group A: info 3
 * Note: pinned columns gets into a new specific "pinned" column
 */
export function infosToGroupsKeepingOrder(initialGroups: I.Group[], orderedInfos: I.Info[], columns: ColumnsState) {
  const infoIdToGroup = initialGroups.reduce<InfoIdToGroup>((acc, group) => {
    const newAcc = { ...acc };
    group.infos.forEach((info) => {
      newAcc[info.id] = {
        id: group.id, label: group.label, denotation: group.denotation, infos: [], visible: group.visible,
      };
    });
    return newAcc;
  }, {});

  return orderedInfos.reduce<I.Group[]>((acc, info) => {
    const groupForInfo = infoIdToGroup[info.id];
    const lastGroup = arrayLast(acc);
    const isPinned = columns[info.id].pinned;

    if (isPinned) {
      if (lastGroup && lastGroup.id === 'pinned') {
        lastGroup.infos.push(info);
        return [...acc.slice(0, -1), lastGroup];
      }

      const newGroup: I.Group = {
        id: 'pinned',
        label: '',
        infos: [],
        visible: true,
      };
      newGroup.infos.push(info);
      return [...acc, newGroup];
    }

    if (lastGroup && lastGroup.id === groupForInfo.id) {
      lastGroup.infos.push(info);
      return [...acc.slice(0, -1), lastGroup];
    }

    const newGroup = deepClone(groupForInfo);
    newGroup.infos.push(info);
    return [...acc, newGroup];
  }, []);
}

export function hasMetaInfosWithMultiUnit(meta: I.Meta) {
  return metaInfoInfos(meta).some((i) => (i.units || []).length > 1);
}

export function availableColumnVisibilitySetsAsOptions(
  availableColumnVisibilitySets: I.ColumnVisibilitySet[],
): ValueLabelOption[] | undefined {
  if (availableColumnVisibilitySets.some((s) => s.shared)) return undefined;

  const defaultOption = { value: DEFAULT_SETTING_ID, label: I18n.Elements.Table.ModalColsVisibility.chooseSetting };

  return [defaultOption].concat(
    availableColumnVisibilitySets
      .map((set) => ({ value: set.id.toString(), label: set.label || '' })),
  );
}

export function availableColumnVisibilitySetsAsOptionsGroups(
  availableColumnVisibilitySets: I.ColumnVisibilitySet[],
): ValueLabelOptionsGroup[] | undefined {
  if (!availableColumnVisibilitySets.some((s) => s.shared)) return undefined;

  const defaultOption = { value: DEFAULT_SETTING_ID, label: I18n.Elements.Table.ModalColsVisibility.chooseSetting };

  const groups: ValueLabelOptionsGroup[] = [
    { name: 'default', options: [defaultOption] },
    { name: 'Shared', options: [] },
  ];

  availableColumnVisibilitySets.forEach((set) => {
    groups[set.shared ? 1 : 0].options.push({ value: set.id.toString(), label: set.label || '' });
  });

  return groups;
}
